// Shared resolution of an AI suggestion against a select-backed cell's options
// (e.g. Underlying Security, whose option ids are sibling securities'
// security_ref UUIDs and whose labels are their names). The AI metadata usually
// carries the option NAME, sometimes an id; the grid stores the ID and its
// validation NULLS any value that is not a listed option id (conditions.tsx).
// ONE matcher for every consumer - rest preview, accept write, store patch:
// match by id, then by exact name, then by case-insensitive trimmed name.
//
// The Yes/No rows (BOOLEAN_FIELD_ALIASES) and Cash-or-PIK are ALSO select cells,
// but implicit ones: their options are built from YES_NO / DIVIDEND_PAYMENT_TYPE
// with value = INDEX and the cells carry NO options array, so the matcher above
// can never resolve them - normalizeAiSelectValue below owns that value-domain
// mapping for every consumer instead.

import { BOOLEAN_FIELD_ALIASES, DIVIDEND_PAYMENT_TYPE_ALIAS } from 'common/constants/cap-table/capTable';
import { DIVIDEND_PAYMENT_TYPE } from 'common/constants/inputs';
import { normalizeOptionsLedger } from './normalizeOptionsLedger';

export interface AiSelectOption {
  id?: unknown;
  name?: unknown;
}

export interface ResolvedAiOption {
  id: string | number;
  label: string;
}

export const matchAiOption = (
  options: readonly AiSelectOption[] | null | undefined,
  aiValue: unknown
): ResolvedAiOption | null => {
  if (!options?.length || aiValue === null || aiValue === undefined || aiValue === '') {
    return null;
  }
  const wanted = String(aiValue);
  const relaxed = wanted.trim().toLowerCase();
  const match =
    options.find(option => String(option?.id) === wanted || String(option?.name) === wanted) ??
    options.find(option => String(option?.name).trim().toLowerCase() === relaxed);
  if (match?.id === null || match?.id === undefined) {
    return null;
  }
  return { id: match.id as string | number, label: String(match.name ?? match.id) };
};

// Every spelling of a Yes/No value, mapped onto the YES_NO=['No','Yes'] option
// index: the AI label ('No'/'N'/'Yes'/'Y'), the model boolean, and the index
// itself (string or number) - so normalization is idempotent and legacy raw
// values still resolve.
const YES_NO_INDEX: Record<string, number> = { no: 0, n: 0, false: 0, '0': 0, yes: 1, y: 1, true: 1, '1': 1 };

/**
 * Map an AI-suggested value for a Yes/No or Cash-or-PIK row onto the cell's
 * OPTION-INDEX domain (the value the grid stores and getOptionSelected matches
 * on; also the Cash=0/PIK=1 model enum). Returns null for every other alias and
 * for unmappable values - callers keep their current behavior.
 */
export const normalizeAiSelectValue = (alias: string | null | undefined, aiValue: unknown): number | null => {
  if (!alias || aiValue === null || aiValue === undefined) {
    return null;
  }
  const token = String(aiValue).trim().toLowerCase();
  if (BOOLEAN_FIELD_ALIASES.has(alias)) {
    return YES_NO_INDEX[token] ?? null;
  }
  if (alias === DIVIDEND_PAYMENT_TYPE_ALIAS) {
    const index = DIVIDEND_PAYMENT_TYPE.findIndex(label => label.toLowerCase() === token);
    if (index >= 0) {
      return index;
    }
    return token === '0' || token === '1' ? Number(token) : null;
  }
  return null;
};

/**
 * The same mapping in the MODEL/store domain (what the DB and the securities
 * store hold, mirroring the backend's cast_ai_field_value): the Yes/No rows are
 * model BOOLEANS, Cash-or-PIK stays the 0/1 enum int. Null when nothing maps.
 */
export const normalizeAiModelValue = (alias: string | null | undefined, aiValue: unknown): boolean | number | null => {
  const index = normalizeAiSelectValue(alias, aiValue);
  if (index === null) {
    return null;
  }
  return alias != null && BOOLEAN_FIELD_ALIASES.has(alias) ? index === 1 : index;
};

interface OptionsCell {
  alias?: string | null;
  options?: readonly AiSelectOption[] | null;
  aiFieldMetadata?: { value?: unknown } | null;
}

// Cell-level view of the matcher: resolve the cell's own pending AI suggestion
// (cell.aiFieldMetadata.value unless an explicit value is passed) against the
// cell's options.
export const resolveAiOptionValue = (
  cell: OptionsCell | null | undefined,
  aiValue: unknown = cell?.aiFieldMetadata?.value
): ResolvedAiOption | null => matchAiOption(cell?.options, aiValue);

// Accept-time write value for a grid cell: a Yes/No or Cash-or-PIK row writes
// the OPTION INDEX (its storage domain - the raw label would render SELECT
// OPTION and break ==Y/==N formulas); a non-select cell passes the raw AI
// value through; a select cell (options ARRAY present, even empty) must resolve
// to an option id - committing the raw name would just be nulled by the grid
// validation and the accepted value silently lost, so an unresolved suggestion
// is a hard accept error the caller must surface WITHOUT settling the finding.
export const resolveAcceptValue = (cell: OptionsCell): { value?: unknown; error?: string } => {
  const aiValue = cell.aiFieldMetadata?.value;
  if (cell.alias === 'options_ledger') {
    const ledger = normalizeOptionsLedger(aiValue);
    return ledger === undefined ? { error: 'Cannot accept the options ledger: every row needs valid shares and a strike price' } : { value: ledger };
  }
  const normalized = normalizeAiSelectValue(cell.alias, aiValue);
  if (normalized !== null) {
    return { value: normalized };
  }
  if (!Array.isArray(cell.options)) {
    return { value: aiValue };
  }
  const resolved = resolveAiOptionValue(cell);
  return resolved
    ? { value: resolved.id }
    : { error: `Cannot accept "${String(aiValue ?? '')}": no matching security on this cap table` };
};
